/**
 * StepperDeFicha — o controle de ajuste da ficha, dimensionado para o dedo.
 *
 * Nasce em 2026-08-01 (docs/PLANO_FICHA_DENSIDADE.md, mockup aprovado pelo
 * Paulo). Substitui os `−`/`+` de 20-28px que a ficha herdou do desenho para
 * mouse: `−` e `+` com 44px (o piso usável no celular) e o valor no meio
 * TOCÁVEL — toca e o teclado numérico abre, para digitar 12 direto em vez de
 * tocar `+` nove vezes.
 *
 * Um componente só, usado nos cinco lugares que antes repetiam a mesma
 * botoeira (HP atual, HP máx, Pontos, Nível, cada atributo). A memória do
 * projeto guarda o que custa deixar isso espalhar — aqui já nasce reunido.
 */

import { Minus, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface StepperDeFichaProps {
  /** Rótulo acessível — o campo não tem <label> visível próprio. */
  rotulo: string
  value: number
  onChange: (novo: number) => void
  min?: number
  max?: number
  /** Dica discreta à direita do valor (ex.: a faixa "0-10" do atributo). */
  dica?: string
  /** Classe de cor do número (ex.: a cor do atributo, do HP). */
  corValor?: string
  /** Altura menor, para os cards secundários (Pontos/Nível). Ainda 44px de
   *  alvo — só o campo do meio encolhe; os botões não. */
  className?: string
}

export function StepperDeFicha({
  rotulo,
  value,
  onChange,
  min = 0,
  max = 9999,
  dica,
  corValor,
  className,
}: StepperDeFichaProps) {
  const ajustar = (delta: number) =>
    onChange(Math.max(min, Math.min(max, value + delta)))

  return (
    <div className={cn('flex items-stretch gap-2', className)}>
      <Button
        type="button"
        variant="ghost"
        onClick={() => ajustar(-1)}
        disabled={value <= min}
        aria-label={`Diminuir ${rotulo}`}
        className="h-11 w-11 flex-shrink-0 rounded-xl bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 hover:text-rose-400 disabled:opacity-40 dark:text-rose-400"
      >
        <Minus className="h-5 w-5" />
      </Button>

      {/* O valor é o campo: tocar abre o teclado numérico e digita direto.
          `relative` para a dica flutuar à direita sem empurrar o número do
          centro. */}
      <div className="relative min-w-0 flex-1">
        <Input
          type="number"
          inputMode="numeric"
          aria-label={rotulo}
          value={value}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10)
            onChange(Math.max(min, Math.min(max, Number.isNaN(n) ? min : n)))
          }}
          className={cn(
            'no-spinner h-11 w-full text-center text-lg font-bold tabular-nums',
            'bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-700',
            corValor ?? 'text-game-primary dark:text-white'
          )}
        />
        {dica && (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-medium text-game-muted dark:text-zinc-500">
            {dica}
          </span>
        )}
      </div>

      <Button
        type="button"
        variant="ghost"
        onClick={() => ajustar(1)}
        disabled={value >= max}
        aria-label={`Aumentar ${rotulo}`}
        className="h-11 w-11 flex-shrink-0 rounded-xl bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 hover:text-emerald-400 disabled:opacity-40 dark:text-emerald-400"
      >
        <Plus className="h-5 w-5" />
      </Button>
    </div>
  )
}
